/**
 * 3D Domain Adapter for Template Repeater
 *
 * Uses InstancedMesh to render many copies of template geometry with per-instance
 * position/rotation/scale transforms. Dramatically reduces draw calls and node count
 * compared to full graph instantiation.
 */

import type { RepeaterDomainAdapter, AdapterContext } from '@/nodes/Dependencies';
import type { GraphIndex } from '@/runtime/GraphIndex';
import type { MeshEntry } from './buildSceneDescriptor';
import { collectMeshPipeline } from './buildSceneDescriptor';
import { mergeGeometries } from './geometryUtils';
import { getCyclic } from '@/nodes/arrayUtils';
import type { Three } from './types';
import { logger } from '@/logging';

/** Marker for prebuilt instanced objects (duck-typed by scene pipeline) */
export const PREBUILT_MARKER = '__prebuiltInstanced';

export function createThreeAdapter(getThree: () => Three): RepeaterDomainAdapter {
  return {
    outputTypes: ['three:group', 'three:geometry'],

    getTransformEndpoints() {
      return {
        posX:   { value: [], dataType: 'array', label: 'Position X' },
        posY:   { value: [], dataType: 'array', label: 'Position Y' },
        posZ:   { value: [], dataType: 'array', label: 'Position Z' },
        rotX:   { value: [], dataType: 'array', label: 'Rotation X' },
        rotY:   { value: [], dataType: 'array', label: 'Rotation Y' },
        rotZ:   { value: [], dataType: 'array', label: 'Rotation Z' },
        scaleX: { value: [], dataType: 'array', label: 'Scale X' },
        scaleY: { value: [], dataType: 'array', label: 'Scale Y' },
        scaleZ: { value: [], dataType: 'array', label: 'Scale Z' },
      };
    },

    collectTemplateOutput(
      graphIndex: GraphIndex,
      templateId: string,
      outputPaths: { endpointName: string; internalNodeId: string }[]
    ): MeshEntry[] {
      const meshes: MeshEntry[] = [];
      const polylines: any[] = [];
      const prebuilt: any[] = [];
      const animateCallbacks: any[] = [];

      for (const { internalNodeId } of outputPaths) {
        const intKey = graphIndex.nodeKey(templateId, internalNodeId);
        collectMeshPipeline(graphIndex, intKey, meshes, polylines, prebuilt, animateCallbacks);
      }

      return meshes;
    },

    buildInstances(
      templateOutputs: { output: MeshEntry[]; slotIndices: number[] }[],
      transforms: Record<string, any[]>,
      _context: AdapterContext
    ): any[] {
      const three = getThree();
      const t0 = performance.now();
      const results: any[] = [];

      const posX = transforms.posX ?? [];
      const posY = transforms.posY ?? [];
      const posZ = transforms.posZ ?? [];
      const rotX = transforms.rotX ?? [];
      const rotY = transforms.rotY ?? [];
      const rotZ = transforms.rotZ ?? [];
      const scaleX = transforms.scaleX ?? [];
      const scaleY = transforms.scaleY ?? [];
      const scaleZ = transforms.scaleZ ?? [];

      const tmpPosition = new three.Vector3();
      const tmpQuaternion = new three.Quaternion();
      const tmpEuler = new three.Euler();
      const tmpScale = new three.Vector3();
      const tmpMatrix = new three.Matrix4();

      for (const { output: meshEntries, slotIndices } of templateOutputs) {
        if (!meshEntries || meshEntries.length === 0) continue;
        const groupSize = slotIndices.length;

        for (const entry of meshEntries) {
          // Build geometry from the template
          let geometry: any;
          try {
            geometry = entry.buildGeometry();
          } catch (err) {
            logger.error(`[instancedMeshBuilder] buildGeometry threw: ${err}`);
            continue;
          }
          if (!geometry) continue;

          // Handle array (multi-input merge case)
          if (Array.isArray(geometry)) {
            const geos = geometry.filter(Boolean);
            if (geos.length === 0) continue;
            geometry = geos.length === 1 ? geos[0] : mergeGeometries(geos, 0);
            if (!geometry) continue;
          }

          // Bake internal transform stack into geometry vertices
          if (entry.transforms.length > 0) {
            geometry = geometry.clone();
            const composed = new three.Matrix4();
            for (let t = entry.transforms.length - 1; t >= 0; t--) {
              const obj = new three.Object3D();
              entry.transforms[t].apply(obj);
              obj.updateMatrix();
              composed.multiply(obj.matrix);
            }
            geometry.applyMatrix4(composed);
          }

          // Build material
          let material: any;
          try {
            material = entry.buildMaterial();
          } catch (err) {
            logger.error(`[instancedMeshBuilder] buildMaterial threw: ${err}`);
            continue;
          }
          if (!material) continue;

          // Create InstancedMesh
          const instMesh = new three.InstancedMesh(geometry, material, groupSize);
          instMesh.castShadow = entry.shadowConfig.castShadow;
          instMesh.receiveShadow = entry.shadowConfig.receiveShadow;

          // Set per-instance transforms
          for (let localIdx = 0; localIdx < groupSize; localIdx++) {
            const globalIdx = slotIndices[localIdx];

            tmpPosition.set(
              getCyclic(posX, globalIdx, 0),
              getCyclic(posY, globalIdx, 0),
              getCyclic(posZ, globalIdx, 0)
            );
            tmpEuler.set(
              getCyclic(rotX, globalIdx, 0),
              getCyclic(rotY, globalIdx, 0),
              getCyclic(rotZ, globalIdx, 0)
            );
            tmpQuaternion.setFromEuler(tmpEuler);
            tmpScale.set(
              getCyclic(scaleX, globalIdx, 1),
              getCyclic(scaleY, globalIdx, 1),
              getCyclic(scaleZ, globalIdx, 1)
            );

            tmpMatrix.compose(tmpPosition, tmpQuaternion, tmpScale);
            instMesh.setMatrixAt(localIdx, tmpMatrix);
          }

          instMesh.instanceMatrix.needsUpdate = true;
          // Mark as prebuilt so scene pipeline recognizes it
          (instMesh as any)[PREBUILT_MARKER] = true;
          results.push(instMesh);
        }
      }

      const t1 = performance.now();
      logger.info('instancedMeshBuilder', `buildInstances: ${results.length} InstancedMesh objects in ${(t1-t0).toFixed(1)}ms`);

      return results;
    },

    updateInstanceTransforms(
      existingObjects: any[],
      templateOutputs: { output: MeshEntry[]; slotIndices: number[] }[],
      transforms: Record<string, any[]>,
    ): void {
      const three = getThree();
      const posX = transforms.posX ?? [];
      const posY = transforms.posY ?? [];
      const posZ = transforms.posZ ?? [];
      const rotX = transforms.rotX ?? [];
      const rotY = transforms.rotY ?? [];
      const rotZ = transforms.rotZ ?? [];
      const scaleX = transforms.scaleX ?? [];
      const scaleY = transforms.scaleY ?? [];
      const scaleZ = transforms.scaleZ ?? [];

      const tmpPosition = new three.Vector3();
      const tmpQuaternion = new three.Quaternion();
      const tmpEuler = new three.Euler();
      const tmpScale = new three.Vector3();
      const tmpMatrix = new three.Matrix4();

      let objIdx = 0;
      for (const { output: meshEntries, slotIndices } of templateOutputs) {
        if (!meshEntries || meshEntries.length === 0) continue;
        const groupSize = slotIndices.length;

        for (let _e = 0; _e < meshEntries.length; _e++) {
          const instMesh = existingObjects[objIdx++];
          if (!instMesh) continue;

          for (let localIdx = 0; localIdx < groupSize; localIdx++) {
            const globalIdx = slotIndices[localIdx];

            tmpPosition.set(
              getCyclic(posX, globalIdx, 0),
              getCyclic(posY, globalIdx, 0),
              getCyclic(posZ, globalIdx, 0)
            );
            tmpEuler.set(
              getCyclic(rotX, globalIdx, 0),
              getCyclic(rotY, globalIdx, 0),
              getCyclic(rotZ, globalIdx, 0)
            );
            tmpQuaternion.setFromEuler(tmpEuler);
            tmpScale.set(
              getCyclic(scaleX, globalIdx, 1),
              getCyclic(scaleY, globalIdx, 1),
              getCyclic(scaleZ, globalIdx, 1)
            );

            tmpMatrix.compose(tmpPosition, tmpQuaternion, tmpScale);
            instMesh.setMatrixAt(localIdx, tmpMatrix);
          }

          instMesh.instanceMatrix.needsUpdate = true;
        }
      }
    }
  };
}

